/**
 * The small shapes the selection chrome passes around, kept out of the adapter so the editor
 * package does not depend on which host is mounting it.
 */

/** A configured selection action, as the merged catalogue serves it. */
export interface SelectionAction {
  id: string;
  title: string;
  icon: string;
  prompt: string;
  shortcut: string | null;
  showInMenu: boolean;
  sortOrder: number;
  scope: string;
  isBuiltIn: boolean;
}

/** A frame of the refine stream. Mirrors the adapter's `RefineEvent` exactly. */
export type RefineEvent =
  | { type: "thought"; text: string }
  | { type: "delta"; text: string }
  | { type: "result"; text: string }
  | { type: "error"; message: string };

/**
 * A non-empty text selection, with PLAIN-TEXT offsets alongside the ProseMirror positions.
 *
 * The offsets are what a model needs (it never sees node boundaries); the positions are what
 * an apply needs. Carrying both is the whole reason this type exists.
 */
export interface ThinkSelection {
  text: string;
  from: number;
  to: number;
  startOffset: number;
  endOffset: number;
}

/**
 * Which group a configured action belongs to in the menu.
 *
 * `do` actions act on the workspace (make a task, set a reminder) rather than rewriting the
 * selection, so they are listed apart — the same split the kms menu draws, but derived from
 * the action rather than hardcoded next to it.
 */
export type ActionGroup = "quick" | "do";

const DO_ACTION_IDS = new Set(["taskize", "nudge", "ask"]);

/** Actions that are catalogued but not yet wired anywhere — shown disabled, never hidden. */
export const PLANNED_DO_ACTIONS = new Set(["ask"]);

export function actionGroup(action: SelectionAction): ActionGroup {
  return DO_ACTION_IDS.has(action.id) || DO_ACTION_IDS.has(action.id.replace(/^system-/, ""))
    ? "do"
    : "quick";
}

/** How many configured actions render as first-class chips before the rest fold into "More". */
export const QUICK_ACTION_COUNT = 4;

export function groupActions(actions: SelectionAction[]): {
  quick: SelectionAction[];
  doActions: SelectionAction[];
} {
  const visible = actions.filter((action) => action.showInMenu);
  return {
    quick: visible.filter((action) => actionGroup(action) === "quick"),
    doActions: visible.filter((action) => actionGroup(action) === "do"),
  };
}
